import { isEmpty, isEqual, isNil, uniqWith } from 'lodash';

import { getPrefix, getRandomItemFromArray, stripPrefix } from 'utils/helper/common';
import { getInstagramThumbnailUrl } from 'utils/content/thumbnails';
import { getProfileStatus, getSelectedValues } from 'modules/discovery/utils/discovery';

import type {
  AndOrOptions,
  DataStructureCommon,
  DiscoveryContentFilters,
  DiscoveryContentResultImage,
  DiscoveryContentSearchTerm,
  DiscoveryContentSearchTermType,
  DiscoveryContentTracking,
  HelperInstagramImageSize
} from 'types';

export const CHANNEL = 'instagram';
export const DEFAULT_SORT_OPTION = 'most_recent.desc';
export const DEFAULT_LOGICAL_OPTION: AndOrOptions = 'and';
export const ITEMS_PER_PAGE = 25;

export const PREDEFINED_HASHTAGS = [
  'beautiful',
  'beauty',
  'collabary',
  'family',
  'fashion',
  'fashionmodel',
  'fitness',
  'food',
  'happy',
  'interior',
  'kids',
  'lifestyle',
  'love',
  'ootd',
  'outdoors',
  'sport',
  'summer',
  'travel',
  'vegan',
  'yoga'
];

export const DISCOVERY_CONTENT_SORT_OPTIONS = {
  'most_recent.desc': 'most_recent.descending',
  'most_recent.asc': 'most_recent.ascending',
  'engagement_rate.desc': 'engagement_rate.descending',
  'engagement_rate.asc': 'engagement_rate.ascending',
  'likes.desc': 'likes.descending',
  'likes.asc': 'likes.ascending',
  'comments.desc': 'comments.descending',
  'comments.asc': 'comments.ascending',
  'followers.desc': 'followers.descending',
  'followers.asc': 'followers.ascending'
};

export const getPreparedPreloadImages = (
  results: Array<DiscoveryContentResultImage>,
  size: HelperInstagramImageSize = 'm'
): Array<Promise<DiscoveryContentResultImage>> => results.map(item =>
  getInstagramThumbnailUrl(item.url, size, item)
    .then(url => ({
      ...item,
      assetUrl: url
    }))
    .catch(() => ({ ...item, assetUrl: null })));

export const getSelectedGender = (genders: Array<DataStructureCommon>): string | undefined | null => {
  const selectedGenders = genders.filter(g => g.selected);

  if (!isEmpty(selectedGenders) && selectedGenders.length < 2) {
    return selectedGenders[0].identifier;
  }

  return null;
};

export const isTimeframeReady = (timeframe: DiscoveryContentTracking): boolean => !isNil(timeframe.startDate) && !isNil(timeframe.endDate);

export const areFilteredSearchTermsEqual = (curValue: string, prevValue: string): boolean => isEqual(curValue.toLowerCase(), prevValue.toLowerCase());

export const getPredefinedHashtag = (): Array<string> => [`#${getRandomItemFromArray(PREDEFINED_HASHTAGS)}`];

export const getUniqueSearchTerms = (searchTerms: Array<string>): Array<string> => uniqWith(searchTerms, areFilteredSearchTermsEqual);

export const getSearchTermType = (value: string): DiscoveryContentSearchTermType => {
  const prefix = getPrefix(value);

  if (prefix === '@') {
    return 'mention';
  }

  return 'hashtag';
};

export const getSearchTerms = (searchTerms: Array<string>): Array<DiscoveryContentSearchTerm> => {
  const uniqueSearchTerms = getUniqueSearchTerms(searchTerms);

  return uniqueSearchTerms.map(searchTerm => {
    const keyword = searchTerm.toLowerCase();

    return ({
      keyword: stripPrefix(keyword),
      type: getSearchTermType(searchTerm)
    });
  });
};

export const buildQueryObjectFromFilters = (filters: DiscoveryContentFilters): any => {
  const followers = filters.followers.transformed;
  const gender = getSelectedGender(filters.genders);
  const profileStatus = getProfileStatus(filters.profiles, 'content');
  const timeframe = filters.timeframe;

  return {
    countries: getSelectedValues(filters.countries),
    endDate: timeframe.endDate,
    followersMin: followers?.min,
    followersMax: followers?.max,
    startDate: timeframe.startDate,
    searchTerms: filters.searchTerms,
    searchTermsLogicalOperator: filters.searchTermsLogicalOperator,
    ...(gender ? { gender } : null),
    // $FlowIgnore - Computing object literal may lead to an exponentially large number of cases to reason about because both conditionals are unions
    ...(profileStatus.verified ? { verified: true } : null),
    ...(profileStatus.unverified ? { unverified: true } : null)
  };
};
